import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ConfirmProvider } from "./contexts/ConfirmContext";
import { BrandingProvider } from "./contexts/BrandingContext";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import PlatformLoginPage from "./pages/PlatformLoginPage";
import PlatformDashboardPage from "./pages/PlatformDashboardPage";
import OnboardingPage from "./pages/OnboardingPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import DashboardPage from "./pages/DashboardPage";
import CustomersPage from "./pages/CustomersPage";
import CustomerDetailPage from "./pages/CustomerDetailPage";
import UsersPage from "./pages/UsersPage";
import GlobalTicketsPage from "./pages/GlobalTicketsPage";
import ActivityPage from "./pages/ActivityPage";
import SettingsPage from "./pages/SettingsPage";
import ProfilePage from "./pages/ProfilePage";
import CalendarPage from "./pages/CalendarPage";
import InventoryPage from "./pages/InventoryPage";
import SecurityPage from "./pages/SecurityPage";
import Layout from "./components/Layout";

function ProtectedRoute({ children, adminOnly }) {
  const { user } = useAuth();
  if (user === undefined) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Laden...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem("daton-theme");
    if (stored === "dark") return true;
    if (stored === "light") return false;
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
    localStorage.setItem("daton-theme", darkMode ? "dark" : "light");
  }, [darkMode]);

  if (user === undefined) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Laden...</div>;

  // Platform admin lives on its own /platform/* tree
  if (user?.is_platform_admin) {
    return (
      <Routes>
        <Route path="/platform/login" element={<PlatformLoginPage />} />
        <Route path="/platform/dashboard" element={<PlatformDashboardPage />} />
        <Route path="*" element={<Navigate to="/platform/dashboard" replace />} />
      </Routes>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/platform/login" element={<PlatformLoginPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Workspace user that hasn't completed onboarding → wizard
  if (user && user.onboarded === false) {
    return (
      <Routes>
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="*" element={<Navigate to="/onboarding" replace />} />
      </Routes>
    );
  }

  return (
    <Layout darkMode={darkMode} setDarkMode={setDarkMode}>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/login" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={
          <ProtectedRoute><DashboardPage /></ProtectedRoute>
        } />
        <Route path="/customers" element={
          <ProtectedRoute><CustomersPage /></ProtectedRoute>
        } />
        <Route path="/customers/:id" element={
          <ProtectedRoute><CustomerDetailPage /></ProtectedRoute>
        } />
        <Route path="/tickets" element={
          <ProtectedRoute><GlobalTicketsPage /></ProtectedRoute>
        } />
        <Route path="/calendar" element={
          <ProtectedRoute><CalendarPage /></ProtectedRoute>
        } />
        <Route path="/inventory" element={
          <ProtectedRoute><InventoryPage /></ProtectedRoute>
        } />
        <Route path="/activity" element={
          <ProtectedRoute><ActivityPage /></ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute><ProfilePage /></ProtectedRoute>
        } />
        <Route path="/security" element={
          <ProtectedRoute><SecurityPage /></ProtectedRoute>
        } />
        <Route path="/users" element={
          <ProtectedRoute adminOnly><UsersPage /></ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute adminOnly><SettingsPage /></ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrandingProvider>
        <ConfirmProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </ConfirmProvider>
      </BrandingProvider>
    </AuthProvider>
  );
}

export default App;
